import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchOrder, fetchOrders, updateFulfillmentRemark } from '../../api/orders';
import { useNow } from '../orders/useNow';
import { SellerCentralOrderDate } from '../orders/orderDateDisplay';
import { OrderStatusBadge } from '../orders/orderStatus';
import { OrderLineProducts } from '../../components/ui/OrderLineProducts';
import {
  Badge,
  StatusBanner,
  PageLoading,
  inputClassName,
  panelClassName,
  tableCellClassName,
  tableHeadClassName,
  secondaryButtonClassName,
} from '../../components/ui';
import {
  getPriorityOrderIds,
  subscribePriorityOrderIds,
  removePriorityOrderId,
  addPriorityOrderId,
} from './priorityOrdersStorage';
import { fetchProcurementBatches, updateProcurementBatch, fetchRecentProcurementRemarkHistory } from '../../api/procurement';
import { ProductThumb } from '../../components/ui/ProductThumb';
import { CopyableValue } from '../../components/ui/CopyableValue';
import { useNavigate } from 'react-router-dom';
import { saveProcurementDraft } from '../orders/procurementDraft';
import RemarkHistoryModal, { RemarkHistoryButton } from '../procurement/RemarkHistoryModal';

const filters = [
  { id: 'all', label: 'All orders' },
  { id: 'pending', label: 'Needs confirmation' },
  { id: 'priority', label: 'Priority' },
  { id: 'swapped', label: 'Swapped' },
  { id: 'confirmed', label: 'Confirmed' },
];

const NON_SELLABLE_REASON_LABELS = {
  missing_quantity: 'Missing quantity',
  damaged: 'Damaged',
  swapped: 'Swapped',
  cancelled: 'Cancelled',
  return_by_buyer: 'Return by buyer',
};

const PAGE_SIZE = 25;
const STATS_PAGE_SIZE = 50;

function SearchIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="m16 16 4 4" strokeLinecap="round" />
    </svg>
  );
}

function RefreshIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 7v5h-5M4 17v-5h5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 9A7 7 0 0 0 6 6.5L4 9M5.5 15A7 7 0 0 0 18 17.5l2-2.5" strokeLinecap="round" />
    </svg>
  );
}

function StatCard({ label, value, detail, tone = 'blue' }) {
  const tones = {
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    emerald: 'bg-emerald-500',
  };

  return (
    <article className={`${panelClassName} relative overflow-hidden p-5`}>
      <span className={`absolute inset-y-0 left-0 w-1 ${tones[tone]}`} aria-hidden="true" />
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <strong className="text-3xl font-semibold tracking-tight text-slate-900">{value}</strong>
        <span className="pb-1 text-xs text-slate-500">{detail}</span>
      </div>
    </article>
  );
}

function ValidationAlertModal({ isOpen, onClose, title, message, missingOrders }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="crm-panel relative w-full max-w-md overflow-hidden border border-slate-200 bg-white shadow-2xl rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-1.5 bg-amber-500" />
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-amber-50 text-amber-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 id="alert-modal-title" className="text-base font-semibold text-slate-900">
                {title || 'Validation Error'}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {message}
              </p>
              {missingOrders && missingOrders.length > 0 && (
                <div className="mt-3 max-h-36 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 block mb-1.5">Affected Order IDs</span>
                  <ul className="space-y-1 font-mono text-xs text-slate-700">
                    {missingOrders.map((orderId) => (
                      <li key={orderId} className="flex items-center gap-1.5">
                        <span className="h-1 w-1 rounded-full bg-slate-400 shrink-0" />
                        {orderId}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end border-t border-slate-100 bg-slate-50/50 px-6 py-4">
          <button
            ref={closeButtonRef}
            type="button"
            className="rounded-md border border-transparent bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none"
            onClick={onClose}
          >
            Acknowledge
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomerServicePage({ token }) {
  const now = useNow();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState(1);
  const [days, setDays] = useState('90');
  const [status, setStatus] = useState({ type: 'loading', message: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [priorityIds, setPriorityIds] = useState(() => getPriorityOrderIds());
  const [procurementBatches, setProcurementBatches] = useState([]);
  const [procurementNotes, setProcurementNotes] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    missingOrders: [],
  });
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [remarkHistoryTarget, setRemarkHistoryTarget] = useState(null);
  const [savingRemarkLineId, setSavingRemarkLineId] = useState('');
  const [orderRemarkFallback, setOrderRemarkFallback] = useState({});
  const [fulfillmentRemarkDrafts, setFulfillmentRemarkDrafts] = useState({});
  const [savingFulfillmentRemarkId, setSavingFulfillmentRemarkId] = useState('');

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeFilter]);

  useEffect(() => {
    if (procurementBatches.length) {
      const initialNotes = {};
      for (const batch of procurementBatches) {
        for (const line of batch.lines) {
          const orderId = line.orderItem?.order?.id || line.orderItem?.orderId;
          if (orderId && line.supplierNotes) {
            initialNotes[orderId] = line.supplierNotes;
          }
        }
      }
      setProcurementNotes((prev) => {
        const next = { ...prev };
        for (const [key, val] of Object.entries(initialNotes)) {
          if (next[key] === undefined) {
            next[key] = val;
          }
        }
        return next;
      });
    }
  }, [procurementBatches]);

  const loadProcurement = useCallback(async () => {
    try {
      const allBatches = [];
      let page = 1;
      let totalPages = 1;

      do {
        const payload = await fetchProcurementBatches(token, {
          days: 'all',
          page: String(page),
          pageSize: '50',
        });
        allBatches.push(...(payload.batches || []));
        totalPages = Math.max(1, Number(payload.pagination?.totalPages) || 1);
        page += 1;
      } while (page <= totalPages && page <= 40);

      setProcurementBatches(allBatches);
    } catch (error) {
      console.error('Failed to load procurement batches in Customer Service:', error);
    }
  }, [token]);

  useEffect(() => {
    if (activeFilter === 'priority' || activeFilter === 'swapped') {
      loadProcurement();
    }
  }, [activeFilter, loadProcurement, priorityIds]);

  // When the procurement line was removed on older "Confirm → CS" flows,
  // recover the latest remark from continuous history by CRM order id.
  useEffect(() => {
    if (activeFilter !== 'priority' || !token || !priorityIds.length) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        priorityIds.map(async (orderId) => {
          try {
            const payload = await fetchRecentProcurementRemarkHistory(token, { orderId });
            const latest = (payload.entries || [])[0];
            const text = String(latest?.newRemark || '').trim();
            return [orderId, text];
          } catch {
            return [orderId, ''];
          }
        })
      );

      if (cancelled) return;

      setOrderRemarkFallback((prev) => {
        const next = { ...prev };
        for (const [orderId, text] of results) {
          if (text) next[orderId] = text;
        }
        return next;
      });

      setProcurementNotes((prev) => {
        const next = { ...prev };
        for (const [orderId, text] of results) {
          if (text && next[orderId] === undefined) {
            next[orderId] = text;
          }
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [activeFilter, priorityIds, token, procurementBatches]);

  const [confirmedIds, setConfirmedIds] = useState(() => {
    try {
      const stored = localStorage.getItem('crm_confirmed_orders');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // All delivered order IDs in the current date/search scope (for accurate tab/stat counts).
  const [deliveredIds, setDeliveredIds] = useState([]);
  const [deliveredTotal, setDeliveredTotal] = useState(0);
  const [damagedOrderIds, setDamagedOrderIds] = useState([]);
  const [missingQuantityOrderIds, setMissingQuantityOrderIds] = useState([]);
  const [swappedOrderIds, setSwappedOrderIds] = useState([]);
  const [dispositionOrders, setDispositionOrders] = useState([]);

  useEffect(() => {
    localStorage.setItem('crm_confirmed_orders', JSON.stringify(confirmedIds));
  }, [confirmedIds]);

  useEffect(
    () =>
      subscribePriorityOrderIds((ids) => {
        setPriorityIds((prev) => {
          if (prev.length === ids.length && prev.every((id, index) => id === ids[index])) {
            return prev;
          }
          return ids;
        });
      }),
    []
  );

  // Durable CS queues from Fulfillment non-sellable reasons.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const [damagedPayload, missingQtyPayload, swappedPayload] = await Promise.all([
          fetchOrders(token, {
            nonSellableReason: 'damaged',
            page: '1',
            pageSize: '100',
            days: days || '90',
          }),
          fetchOrders(token, {
            nonSellableReason: 'missing_quantity',
            page: '1',
            pageSize: '100',
            days: days || '90',
          }),
          fetchOrders(token, {
            nonSellableReason: 'swapped',
            page: '1',
            pageSize: '100',
            days: days || '90',
          }),
        ]);
        if (cancelled) return;

        const damaged = damagedPayload.orders || [];
        const missingQuantity = missingQtyPayload.orders || [];
        const swapped = swappedPayload.orders || [];
        setDamagedOrderIds(damaged.map((order) => order.id));
        setMissingQuantityOrderIds(missingQuantity.map((order) => order.id));
        setSwappedOrderIds(swapped.map((order) => order.id));
        setDispositionOrders([...damaged, ...missingQuantity, ...swapped]);

        for (const order of [...damaged, ...missingQuantity]) {
          addPriorityOrderId(order.id);
        }
      } catch (error) {
        console.error('Failed to load disposition orders for Customer Service:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, days]);

  const workspaceOrders = useMemo(() => {
    const byId = new Map(orders.map((order) => [order.id, order]));
    for (const order of dispositionOrders) {
      const existing = byId.get(order.id);
      byId.set(order.id, existing ? { ...existing, ...order } : order);
    }
    return Array.from(byId.values());
  }, [orders, dispositionOrders]);

  const loadOrders = useCallback(async () => {
    setStatus({ type: 'loading', message: '' });
    try {
      const params = {
        status: 'delivered',
        page: String(page),
        pageSize: String(PAGE_SIZE),
        days: days || '90',
      };
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const payload = await fetchOrders(token, params);
      const delivered = payload.orders || [];
      setPagination(
        payload.pagination || {
          page,
          pageSize: PAGE_SIZE,
          total: delivered.length,
          totalPages: 1,
        }
      );

      const deliveredIdsOnPage = new Set(delivered.map((order) => order.id));
      // Cap extras so priority queue never balloons the page beyond the current page size.
      const missingPriorityIds = getPriorityOrderIds()
        .filter((id) => !deliveredIdsOnPage.has(id))
        .slice(0, PAGE_SIZE);

      const priorityExtras = (
        await Promise.all(
          missingPriorityIds.map(async (id) => {
            try {
              const result = await fetchOrder(token, id);
              return result.order || null;
            } catch {
              return null;
            }
          })
        )
      ).filter(Boolean);

      const merged = [...priorityExtras, ...delivered];
      const seen = new Set();
      setOrders(
        merged.filter((order) => {
          if (seen.has(order.id)) return false;
          seen.add(order.id);
          return true;
        })
      );
      setStatus({ type: 'ready', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  }, [token, page, days, searchQuery]);

  const loadDeliveredIdsForCounts = useCallback(async () => {
    try {
      const baseParams = {
        status: 'delivered',
        pageSize: String(STATS_PAGE_SIZE),
        days: days || '90',
      };
      if (searchQuery.trim()) baseParams.search = searchQuery.trim();

      const first = await fetchOrders(token, { ...baseParams, page: '1' });
      const total = first.pagination?.total ?? (first.orders || []).length;
      const totalPages = Math.max(1, first.pagination?.totalPages || 1);
      const ids = (first.orders || []).map((order) => order.id);

      for (let nextPage = 2; nextPage <= totalPages; nextPage += 1) {
        const payload = await fetchOrders(token, {
          ...baseParams,
          page: String(nextPage),
        });
        for (const order of payload.orders || []) {
          ids.push(order.id);
        }
      }

      setDeliveredIds(ids);
      setDeliveredTotal(total);
    } catch (error) {
      console.error('Failed to load confirmation counts:', error);
    }
  }, [token, days, searchQuery]);

  useEffect(() => {
    loadDeliveredIdsForCounts();
  }, [loadDeliveredIdsForCounts]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery((current) => {
        if (current === searchInput) return current;
        setPage(1);
        return searchInput;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // When procurement pushes a new priority order, pull a bounded set into the queue.
  useEffect(() => {
    const missing = priorityIds
      .filter((id) => !orders.some((order) => order.id === id))
      .slice(0, PAGE_SIZE);
    if (!missing.length) return undefined;

    let cancelled = false;
    (async () => {
      const extras = (
        await Promise.all(
          missing.map(async (id) => {
            try {
              const result = await fetchOrder(token, id);
              return result.order || null;
            } catch {
              return null;
            }
          })
        )
      ).filter(Boolean);

      if (cancelled || !extras.length) return;
      setOrders((prev) => {
        const existing = new Set(prev.map((order) => order.id));
        const merged = [
          ...extras.filter((order) => !existing.has(order.id)),
          ...prev,
        ];
        return merged.slice(0, PAGE_SIZE * 2);
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [orders, priorityIds, token]);

  const toggleConfirmation = (id) => {
    setConfirmedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const findProcurementInfo = useCallback((order) => {
    if (!order?.id) return null;

    const orderItemIds = new Set(
      (order.items || []).map((item) => item?.id).filter(Boolean)
    );
    const amazonOrderId = order.amazonOrderId || '';

    const matches = [];
    for (const batch of procurementBatches) {
      for (const line of batch.lines || []) {
        const nestedOrder = line.orderItem?.order;
        const nestedOrderId = nestedOrder?.id || line.orderItem?.orderId || null;
        const nestedAmazonId = nestedOrder?.amazonOrderId || '';
        const lineOrderItemId = line.orderItemId || null;

        const matched =
          (nestedOrderId && nestedOrderId === order.id) ||
          (lineOrderItemId && orderItemIds.has(lineOrderItemId)) ||
          (amazonOrderId && nestedAmazonId && nestedAmazonId === amazonOrderId);

        if (!matched) continue;

        const status = getUIStatus(line.inventoryStatus);
        let score = 0;
        if (status === 'Customer Service') score += 2;
        if (line.confirmed) score += 1;
        if (nestedOrderId === order.id) score += 1;
        if (line.supplierNotes) score += 2;
        matches.push({ line, batch, score });
      }
    }

    if (!matches.length) return null;

    matches.sort((a, b) => b.score - a.score);
    return { line: matches[0].line, batch: matches[0].batch };
  }, [procurementBatches]);

  const getProcurementLineForOrder = useCallback((order) => {
    const info = findProcurementInfo(order);
    if (info) {
      return info.line;
    }
    const primaryItem = order.items?.[0];
    const fallbackRemark = orderRemarkFallback[order.id] || '';
    return {
      id: `manual-${order.id}`,
      batchId: 'manual',
      orderItemId: primaryItem?.id || null,
      inventoryItemId: null,
      quantity: primaryItem?.quantityOrdered || 1,
      extraQuantity: 0,
      inventoryStatus: 'Customer Service',
      supplierNotes: fallbackRemark,
      companyName: null,
      productLink: null,
      usOrderId: null,
      usTrackingId: null,
      sellingPrice: primaryItem?.unitPrice || 0,
      purchasePrice: 0,
      confirmed: false,
      orderItem: primaryItem ? {
        ...primaryItem,
        order: order
      } : undefined
    };
  }, [findProcurementInfo, orderRemarkFallback]);

  const isSavedProcurementLineId = (lineId) =>
    Boolean(lineId) && !String(lineId).startsWith('manual-');

  const handleRemarkDraftChange = (lineId, value) => {
    setRemarkDrafts((prev) => ({ ...prev, [lineId]: value }));
  };

  const getRemarkValue = (line, orderId) => {
    if (!line?.id) return '';
    if (Object.prototype.hasOwnProperty.call(remarkDrafts, line.id)) {
      return remarkDrafts[line.id];
    }
    return line.supplierNotes || orderRemarkFallback[orderId] || '';
  };

  const handleRemarkSave = async (order) => {
    const info = findProcurementInfo(order);
    if (!info?.line?.id || !info?.batch?.id) {
      return;
    }
    if (info.batch.status === 'received' || info.batch.status === 'cancelled') {
      setStatus({
        type: 'error',
        message: 'Remarks cannot be edited on received or cancelled batches.',
      });
      return;
    }

    const lineId = info.line.id;
    const nextValue = String(getRemarkValue(info.line, order.id) || '').trim();
    const previousValue = String(
      info.line.supplierNotes || orderRemarkFallback[order.id] || ''
    ).trim();
    if (nextValue === previousValue) {
      setRemarkDrafts((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
      return;
    }

    setSavingRemarkLineId(lineId);
    try {
      const updatedLines = (info.batch.lines || []).map((line) => ({
        id: line.id,
        orderItemId: line.orderItemId || undefined,
        inventoryItemId: line.inventoryItemId || undefined,
        companyName: line.companyName || undefined,
        productLink: line.productLink || undefined,
        quantity: line.quantity,
        extraQuantity: line.extraQuantity || 0,
        supplierNotes: line.id === lineId ? nextValue : line.supplierNotes || '',
        usOrderId: line.usOrderId || undefined,
        usTrackingId: line.usTrackingId || undefined,
        hawb: line.hawb || undefined,
        sellingPrice: line.sellingPrice ? Number(line.sellingPrice) : 0,
        purchasePrice: line.purchasePrice ? Number(line.purchasePrice) : 0,
        buyMarketplace: line.buyMarketplace || 'US',
        buyCarrierName: line.buyCarrierName || undefined,
        inventoryStatus: getUIStatus(line.inventoryStatus),
        confirmed: line.confirmed || false,
      }));

      await updateProcurementBatch(token, info.batch.id, { lines: updatedLines });
      setRemarkDrafts((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
      setStatus({ type: 'success', message: 'Remark saved.' });
      await loadProcurement();
    } catch (error) {
      setRemarkDrafts((prev) => ({ ...prev, [lineId]: previousValue }));
      setStatus({ type: 'error', message: error.message || 'Failed to save remark.' });
    } finally {
      setSavingRemarkLineId('');
    }
  };

  const getFulfillmentRemarkValue = (order) => {
    if (!order?.id) return '';
    if (Object.prototype.hasOwnProperty.call(fulfillmentRemarkDrafts, order.id)) {
      return fulfillmentRemarkDrafts[order.id];
    }
    return order.fulfillmentRemark || '';
  };

  const handleFulfillmentRemarkDraftChange = (orderId, value) => {
    setFulfillmentRemarkDrafts((prev) => ({ ...prev, [orderId]: value }));
  };

  const patchOrderFulfillmentRemark = (orderId, remark) => {
    const nextRemark = remark || null;
    setOrders((current) =>
      (current || []).map((row) =>
        row.id === orderId ? { ...row, fulfillmentRemark: nextRemark } : row
      )
    );
    setDispositionOrders((current) =>
      (current || []).map((row) =>
        row.id === orderId ? { ...row, fulfillmentRemark: nextRemark } : row
      )
    );
  };

  const handleFulfillmentRemarkSave = async (order) => {
    const nextValue = String(getFulfillmentRemarkValue(order) || '').trim();
    const previousValue = String(order.fulfillmentRemark || '').trim();
    if (nextValue === previousValue) {
      setFulfillmentRemarkDrafts((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
      return;
    }

    setSavingFulfillmentRemarkId(order.id);
    try {
      const payload = await updateFulfillmentRemark(token, order.id, nextValue);
      const savedValue = payload.order?.fulfillmentRemark || '';
      patchOrderFulfillmentRemark(order.id, savedValue);
      setFulfillmentRemarkDrafts((prev) => ({ ...prev, [order.id]: savedValue }));
      setStatus({ type: 'success', message: 'Remark saved.' });
    } catch (error) {
      setFulfillmentRemarkDrafts((prev) => ({ ...prev, [order.id]: previousValue }));
      setStatus({ type: 'error', message: error.message || 'Failed to save remark.' });
    } finally {
      setSavingFulfillmentRemarkId('');
    }
  };


  const handleSendToProcurement = useCallback((order) => {
    const items = order.items || [];
    if (!items.length) {
      setAlertModal({
        isOpen: true,
        title: 'No Line Items',
        message: 'This order has no line items to send to procurement.',
        missingOrders: [],
      });
      return;
    }

    const linkedRemark = String(findProcurementInfo(order)?.line?.supplierNotes || '').trim();
    const note = (procurementNotes[order.id] || linkedRemark).trim();
    if (!note) {
      setAlertModal({
        isOpen: true,
        title: 'Missing Procurement Note',
        message: 'Procurement Note is mandatory to send this order to procurement. Please fill out the Procurement Note field.',
        missingOrders: [order.amazonOrderId || order.id],
      });
      return;
    }

    const lines = items.map((item) => ({
      orderId: order.id,
      orderItemId: item.id,
      orderItem: { ...item, order },
      order,
      amazonOrderId: order.amazonOrderId,
      buyerName: order.buyerName,
      quantity: item.quantityOrdered || 1,
      extraQuantity: 0,
      productLink: '',
      supplierNotes: note,
      isReorder: true,
    }));

    saveProcurementDraft(lines);
    removePriorityOrderId(order.id);
    setPriorityIds((prev) => prev.filter((id) => id !== order.id));
    navigate('/admin/procurement?from=orders');
  }, [findProcurementInfo, navigate, procurementNotes]);

  const isPriorityOrder = useCallback(
    (order) =>
      Boolean(
        order &&
          (priorityIds.includes(order.id) ||
            order.nonSellableReason === 'damaged' ||
            order.nonSellableReason === 'missing_quantity' ||
            damagedOrderIds.includes(order.id) ||
            missingQuantityOrderIds.includes(order.id))
      ),
    [priorityIds, damagedOrderIds, missingQuantityOrderIds]
  );

  const getPriorityRowHighlightClass = useCallback((order) => {
    const reason = order?.nonSellableReason;
    if (reason === 'missing_quantity' || missingQuantityOrderIds.includes(order?.id)) {
      return 'bg-amber-50 hover:bg-amber-100/80';
    }
    if (reason === 'damaged' || damagedOrderIds.includes(order?.id)) {
      return 'bg-red-50 hover:bg-red-100/80';
    }
    if (isPriorityOrder(order)) {
      return 'bg-red-50 hover:bg-red-100/80';
    }
    return '';
  }, [missingQuantityOrderIds, damagedOrderIds, isPriorityOrder]);

  const isSwappedOrder = useCallback(
    (order) =>
      Boolean(
        order &&
          (order.nonSellableReason === 'swapped' || swappedOrderIds.includes(order.id))
      ),
    [swappedOrderIds]
  );

  const counts = useMemo(() => {
    const confirmedSet = new Set(confirmedIds);
    let confirmed = 0;
    for (const id of deliveredIds) {
      if (confirmedSet.has(id)) {
        confirmed += 1;
      }
    }
    const total = deliveredTotal || pagination.total || orders.length;
    const prioritySet = new Set([
      ...priorityIds,
      ...damagedOrderIds,
      ...missingQuantityOrderIds,
    ]);
    return {
      total,
      confirmed,
      pending: Math.max(0, total - confirmed),
      priority: prioritySet.size,
      swapped: swappedOrderIds.length,
    };
  }, [
    confirmedIds,
    deliveredIds,
    deliveredTotal,
    pagination.total,
    orders.length,
    priorityIds,
    damagedOrderIds,
    missingQuantityOrderIds,
    swappedOrderIds,
  ]);

  const visibleOrders = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const cleanQuery = query.replace(/[^a-z0-9]/g, '');

    return workspaceOrders
      .filter((order) => {
        if (activeFilter === 'pending') return !confirmedIds.includes(order.id);
        if (activeFilter === 'priority') return isPriorityOrder(order);
        if (activeFilter === 'swapped') return isSwappedOrder(order);
        if (activeFilter === 'confirmed') return confirmedIds.includes(order.id);
        return true;
      })
      .filter((order) => {
        // Extra client refine for confirmation/priority tabs (server already searched).
        if (!query) return true;

        const orderIdMatch =
          order.amazonOrderId?.toLowerCase().includes(query) ||
          (order.amazonOrderId &&
            order.amazonOrderId.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().includes(cleanQuery));

        const nameMatch = order.buyerName?.toLowerCase().includes(query);
        const phoneMatch =
          order.buyerPhone?.toLowerCase().includes(query) ||
          (order.buyerPhone &&
            order.buyerPhone.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().includes(cleanQuery));
        const cityMatch = order.shipCity?.toLowerCase().includes(query);
        const trackingMatch = order.trackingUs?.toLowerCase().includes(query);
        const orderMatch = orderIdMatch || nameMatch || phoneMatch || cityMatch || trackingMatch;
        const itemMatch = order.items?.some((item) =>
          [item.title, item.sku, item.asinIn, item.asinCom].some((value) =>
            value?.toLowerCase().includes(query)
          )
        );
        return orderMatch || itemMatch;
      })
      .sort((a, b) => Number(isPriorityOrder(b)) - Number(isPriorityOrder(a)));
  }, [
    activeFilter,
    confirmedIds,
    workspaceOrders,
    searchQuery,
    isPriorityOrder,
    isSwappedOrder,
  ]);

  const allSelected = useMemo(
    () => visibleOrders.length > 0 && selectedIds.size === visibleOrders.length,
    [visibleOrders, selectedIds]
  );

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(visibleOrders.map((order) => order.id)));
  }, [allSelected, visibleOrders]);

  const toggleOne = useCallback((orderId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }, []);

  const handleSendMultipleToProcurement = useCallback(() => {
    const selectedOrders = visibleOrders.filter((order) => selectedIds.has(order.id));

    if (!selectedOrders.length) {
      setAlertModal({
        isOpen: true,
        title: 'No Orders Selected',
        message: 'Please select at least one order to send to procurement.',
        missingOrders: [],
      });
      return;
    }

    const ordersWithoutNote = [];
    const lines = [];

    for (const order of selectedOrders) {
      const linkedRemark = String(findProcurementInfo(order)?.line?.supplierNotes || '').trim();
      const note = (procurementNotes[order.id] || linkedRemark).trim();
      if (!note) {
        ordersWithoutNote.push(order.amazonOrderId || order.id);
        continue;
      }

      const items = order.items || [];
      for (const item of items) {
        lines.push({
          orderId: order.id,
          orderItemId: item.id,
          orderItem: { ...item, order },
          order,
          amazonOrderId: order.amazonOrderId,
          buyerName: order.buyerName,
          quantity: item.quantityOrdered || 1,
          extraQuantity: 0,
          productLink: '',
          supplierNotes: note,
          isReorder: true,
        });
      }
    }

    if (ordersWithoutNote.length) {
      setAlertModal({
        isOpen: true,
        title: 'Missing Procurement Notes',
        message: 'Procurement Note is mandatory for all selected orders. Please fill out the Procurement Note for the affected orders before sending.',
        missingOrders: ordersWithoutNote,
      });
      return;
    }

    if (!lines.length) {
      setAlertModal({
        isOpen: true,
        title: 'No Line Items',
        message: 'Selected orders have no line items to send to procurement.',
        missingOrders: [],
      });
      return;
    }

    saveProcurementDraft(lines);
    const orderIds = selectedOrders.map((o) => o.id);
    for (const orderId of orderIds) {
      removePriorityOrderId(orderId);
    }
    setPriorityIds((prev) => prev.filter((id) => !orderIds.includes(id)));
    navigate('/admin/procurement?from=orders');
  }, [findProcurementInfo, navigate, procurementNotes, selectedIds, visibleOrders]);

  const total = pagination.total || 0;
  const totalPages = pagination.totalPages || 0;
  const rangeStart = total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.page * pagination.pageSize, total);

  return (
    <section className="grid min-w-0 w-full gap-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Delivered orders" value={counts.total} detail="in workspace" />
        <StatCard label="Needs confirmation" value={counts.pending} detail="follow-ups due" tone="amber" />
        <StatCard label="Priority queue" value={counts.priority} detail="flagged orders" tone="red" />
        <StatCard label="Confirmed" value={counts.confirmed} detail="customers reached" tone="emerald" />
      </div>

      <StatusBanner
        status={
          status.type === 'error'
            ? { type: 'error', message: status.message || 'Unable to load delivered orders.' }
            : status
        }
        setStatus={setStatus}
        action={
          status.type === 'error' ? (
            <button type="button" className="font-semibold hover:underline" onClick={loadOrders}>
              Try again
            </button>
          ) : null
        }
      />

      <div className={`${panelClassName} w-full overflow-hidden`}>
        <div className="border-b border-slate-200 px-5 py-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold tracking-tight text-slate-900">Customer follow-up queue</h2>
              <p className="mt-1 text-sm text-slate-500">Track confirmations and focus the team on orders needing attention.</p>
            </div>
            <div className="flex items-center gap-2">
              {activeFilter === 'priority' && selectedIds.size > 0 && (
                <button
                  type="button"
                  className="rounded-md border border-transparent bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                  onClick={handleSendMultipleToProcurement}
                >
                  Send to procurement ({selectedIds.size})
                </button>
              )}
              <button
                type="button"
                className={`${secondaryButtonClassName} gap-2`}
                onClick={loadOrders}
                disabled={status.type === 'loading'}
              >
                <RefreshIcon className={status.type === 'loading' ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1" role="tablist" aria-label="Filter orders">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  role="tab"
                  aria-selected={activeFilter === filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={[
                    'whitespace-nowrap rounded-md px-3 py-2 text-xs font-semibold transition',
                    activeFilter === filter.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800',
                  ].join(' ')}
                >
                  {filter.label}
                  <span className="ml-2 text-[10px] text-slate-400">
                    {filter.id === 'all' ? counts.total : counts[filter.id]}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:max-w-xl">
              <label className="grid gap-1 sm:w-40">
                <span className="sr-only">Date range</span>
                <select
                  className="crm-input"
                  value={days}
                  onChange={(event) => {
                    setPage(1);
                    setDays(event.target.value);
                  }}
                >
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="180">Last 180 days</option>
                  <option value="all">All time</option>
                </select>
              </label>
              <label className="relative block w-full">
                <span className="sr-only">Search delivered orders</span>
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  className="crm-input pl-9 pr-9"
                  type="search"
                  placeholder="Search by order ID, customer name, or phone number..."
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
                {searchInput ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs font-semibold text-slate-400 hover:text-slate-700"
                    onClick={() => {
                      setSearchInput('');
                      setSearchQuery('');
                      setPage(1);
                    }}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                ) : null}
              </label>
            </div>
          </div>
        </div>

        {status.type === 'loading' && !orders.length ? (
          <div className="p-5">
            <PageLoading label="Loading delivered orders…" />
          </div>
        ) : visibleOrders.length ? (
          <div className="grid gap-0">
          <div className="w-full overflow-x-auto overscroll-x-contain">
            {activeFilter === 'priority' || activeFilter === 'swapped' ? (
              <table className="w-full border-collapse min-w-[84rem]">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="border-b border-slate-200 px-4 py-3 text-left w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    </th>
                    {activeFilter !== 'swapped' ? (
                      <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-28 whitespace-nowrap">Ship By</th>
                    ) : null}
                    <th className="border-b border-slate-200 px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-16">Image</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[280px]">Product Name</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-24 whitespace-nowrap">Ordered Qty</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-28 whitespace-nowrap">Total Value</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-36 whitespace-nowrap">Type</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-28 whitespace-nowrap">Status</th>
                    <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[180px]">Remark</th>
                    {activeFilter !== 'swapped' ? (
                      <th className="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-56 whitespace-nowrap">Procurement Note</th>
                    ) : null}
                    <th className="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-64 whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((order) => {
                    const line = getProcurementLineForOrder(order);
                    const asin = line.orderItem?.asinIn || line.orderItem?.asinCom || line.asin;
                    const sku = line.orderItem?.sku || line.sku;
                    const sellingPriceRealtime = line.sellingPrice || line.orderItem?.unitPrice || 0;
                    const orderedQty = line.orderItem?.quantityOrdered || line.quantity || 1;
                    const totalValue = (line.orderItem?.order?.totalAmount != null && Number(line.orderItem.order.totalAmount) !== 0)
                      ? Number(line.orderItem.order.totalAmount)
                      : (orderedQty * sellingPriceRealtime);
                    const totalValueCurrency = line.orderItem?.order?.currency || 'INR';
                    const orderStatus = line.orderItem?.order?.status;
                    const isPriority = isPriorityOrder(order);
                    const rowHighlight =
                      activeFilter === 'swapped'
                        ? ''
                        : isPriority
                          ? getPriorityRowHighlightClass(order)
                          : '';

                    return (
                      <tr
                        key={order.id}
                        className={[
                          'group transition-colors hover:bg-slate-50/50',
                          rowHighlight,
                        ].join(' ')}
                      >
                        <td className="border-b border-slate-200 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(order.id)}
                            onChange={() => toggleOne(order.id)}
                          />
                        </td>
                        {/* Ship By */}
                        {activeFilter !== 'swapped' ? (
                          <td className={`${tableCellClassName} w-28 whitespace-nowrap font-medium text-slate-700`}>
                            {line.orderItem?.order?.shipByDate ? new Date(line.orderItem.order.shipByDate).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            }) : '—'}
                          </td>
                        ) : null}

                        {/* Image */}
                        <td className={`${tableCellClassName} w-16 text-center`}>
                          {line.orderItem ? (
                            <ProductThumb
                              item={line.orderItem}
                              marketplace={line.orderItem.order?.marketplace}
                              className="h-10 w-10 mx-auto"
                            />
                          ) : (
                            <span className="inline-grid h-10 w-10 shrink-0 place-items-center rounded-md border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-400 mx-auto" aria-hidden="true">
                              SKU
                            </span>
                          )}
                        </td>

                        {/* Product Name */}
                        <td className={`${tableCellClassName} min-w-[280px]`}>
                          <div className="min-w-0">
                            <span className="line-clamp-2 text-sm font-medium text-slate-900 block" title={line.orderItem?.title || 'Manual line'}>
                              {line.orderItem?.title || 'Manual line'}
                            </span>
                            {order.nonSellableReason && NON_SELLABLE_REASON_LABELS[order.nonSellableReason] ? (
                              <span
                                className={[
                                  'mt-1 inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold',
                                  order.nonSellableReason === 'damaged'
                                    ? 'border border-red-200 bg-white text-red-800'
                                    : order.nonSellableReason === 'missing_quantity'
                                      ? 'border border-amber-300 bg-white text-amber-950'
                                      : 'bg-amber-100 text-amber-900',
                                ].join(' ')}
                              >
                                {NON_SELLABLE_REASON_LABELS[order.nonSellableReason]}
                              </span>
                            ) : null}
                            {(activeFilter === 'priority' || activeFilter === 'swapped') && (
                              <span className="mt-1 ml-1 inline-flex rounded-md bg-violet-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-violet-700 ring-1 ring-inset ring-violet-200">
                                Reorder
                              </span>
                            )}
                            {(line.orderItem?.order?.amazonOrderId || asin || sku) && (
                              <span className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-slate-500 block leading-normal">
                                {line.orderItem?.order?.amazonOrderId ? (
                                  <>
                                    <span>Order ID</span>
                                    <CopyableValue value={line.orderItem.order.amazonOrderId} className="hover:bg-slate-100" />
                                    {(asin || sku) && <span aria-hidden="true">·</span>}
                                  </>
                                ) : null}
                                {asin ? (
                                  <>
                                    <CopyableValue label="ASIN" value={asin} className="hover:bg-slate-100" />
                                    {sku && <span aria-hidden="true">·</span>}
                                  </>
                                ) : ''}
                                {sku ? (
                                  <>
                                    <CopyableValue label="SKU" value={sku} className="hover:bg-slate-100" />
                                  </>
                                ) : ''}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Ordered Qty */}
                        <td className={`${tableCellClassName} w-24 text-center whitespace-nowrap`}>
                          {orderedQty}
                        </td>

                        {/* Total Value */}
                        <td className={`${tableCellClassName} w-28 text-right whitespace-nowrap`}>
                          <span className="font-semibold text-emerald-700">
                            {totalValueCurrency === 'USD' ? '$' : totalValueCurrency === 'INR' || totalValueCurrency === '₹' ? '₹' : totalValueCurrency + ' '}
                            {Number(totalValue).toFixed(2)}
                          </span>
                        </td>

                        {/* Type */}
                        <td className={`${tableCellClassName} w-36 whitespace-nowrap`}>
                          <span className="text-sm font-medium text-slate-700">
                            {getUIStatus(line.inventoryStatus)}
                          </span>
                        </td>

                        {/* Status */}
                        <td className={`${tableCellClassName} w-28 whitespace-nowrap`}>
                          {orderStatus ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  getDisplayOrderStatus(orderStatus) === 'delivered'
                                    ? 'bg-emerald-500'
                                    : getDisplayOrderStatus(orderStatus) === 'shipped'
                                      ? 'bg-blue-500'
                                      : getDisplayOrderStatus(orderStatus) === 'cancelled'
                                        ? 'bg-rose-500'
                                        : 'bg-amber-500'
                                }`}
                              />
                              <span
                                className={
                                  getDisplayOrderStatus(orderStatus) === 'delivered'
                                    ? 'text-emerald-800'
                                    : getDisplayOrderStatus(orderStatus) === 'shipped'
                                      ? 'text-blue-800'
                                      : getDisplayOrderStatus(orderStatus) === 'cancelled'
                                        ? 'text-rose-800'
                                        : 'text-amber-800'
                                }
                              >
                                {getDisplayOrderStatus(orderStatus).charAt(0).toUpperCase() +
                                  getDisplayOrderStatus(orderStatus).slice(1)}
                              </span>
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>

                        {/* Remark */}
                        <td className={`${tableCellClassName} min-w-[180px] whitespace-nowrap`}>
                          {activeFilter === 'swapped' ? (
                            <div className="flex items-start gap-2">
                              <input
                                type="text"
                                className={`${inputClassName} min-w-[150px] text-sm`}
                                placeholder="Remarks"
                                value={getFulfillmentRemarkValue(order)}
                                disabled={savingFulfillmentRemarkId === order.id}
                                onChange={(event) =>
                                  handleFulfillmentRemarkDraftChange(order.id, event.target.value)
                                }
                                onBlur={() => handleFulfillmentRemarkSave(order)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') event.currentTarget.blur();
                                }}
                              />
                              <RemarkHistoryButton
                                lineId={isSavedProcurementLineId(line.id) ? line.id : undefined}
                                orderId={order.id}
                                onClick={() =>
                                  setRemarkHistoryTarget(
                                    isSavedProcurementLineId(line.id)
                                      ? { lineId: line.id, orderId: order.id }
                                      : { orderId: order.id }
                                  )
                                }
                              />
                            </div>
                          ) : (
                            (() => {
                              const fulfillmentRemark = String(order.fulfillmentRemark || '').trim();
                              if (fulfillmentRemark) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <span className="min-w-0 flex-1 text-sm text-slate-800" title={fulfillmentRemark}>
                                      {fulfillmentRemark}
                                    </span>
                                    <RemarkHistoryButton
                                      lineId={isSavedProcurementLineId(line.id) ? line.id : undefined}
                                      orderId={order.id}
                                      onClick={() =>
                                        setRemarkHistoryTarget(
                                          isSavedProcurementLineId(line.id)
                                            ? { lineId: line.id, orderId: order.id }
                                            : { orderId: order.id }
                                        )
                                      }
                                    />
                                  </div>
                                );
                              }
                              if (isSavedProcurementLineId(line.id)) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <input
                                      type="text"
                                      className={`${inputClassName} min-w-[150px] text-sm`}
                                      placeholder="Remarks"
                                      value={getRemarkValue(line, order.id)}
                                      disabled={savingRemarkLineId === line.id}
                                      onChange={(event) =>
                                        handleRemarkDraftChange(line.id, event.target.value)
                                      }
                                      onBlur={() => handleRemarkSave(order)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') event.currentTarget.blur();
                                      }}
                                    />
                                    <RemarkHistoryButton
                                      lineId={line.id}
                                      orderId={order.id}
                                      onClick={() =>
                                        setRemarkHistoryTarget({ lineId: line.id, orderId: order.id })
                                      }
                                    />
                                  </div>
                                );
                              }
                              return (
                                <div className="flex items-start gap-2">
                                  <span className="min-w-0 flex-1">
                                    {line.supplierNotes || orderRemarkFallback[order.id] || '—'}
                                  </span>
                                  <RemarkHistoryButton
                                    orderId={order.id}
                                    onClick={() => setRemarkHistoryTarget({ orderId: order.id })}
                                  />
                                </div>
                              );
                            })()
                          )}
                        </td>

                        {/* Procurement Note */}
                        {activeFilter !== 'swapped' ? (
                          <td className={`${tableCellClassName} w-56`}>
                            <input
                              type="text"
                              placeholder="Enter note (mandatory)..."
                              value={procurementNotes[order.id] || ''}
                              onChange={(e) => setProcurementNotes((prev) => ({ ...prev, [order.id]: e.target.value }))}
                              className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none bg-slate-50/50 hover:bg-white focus:bg-white transition"
                            />
                          </td>
                        ) : null}

                        {/* CS Action */}
                        <td className={`${tableCellClassName} text-right w-64 whitespace-nowrap`}>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
                              onClick={() => setSelectedOrder(order)}
                            >
                              Review order
                            </button>
                            {activeFilter !== 'swapped' ? (
                              <button
                                type="button"
                                className="rounded-md border border-blue-200 bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 hover:border-blue-700"
                                onClick={() => handleSendToProcurement(order)}
                              >
                                Send to procurement
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="w-full min-w-[72rem] border-collapse">
                <thead>
                  <tr>
                    <th className={tableHeadClassName}>Order Date</th>
                    <th className={tableHeadClassName}>Order ID</th>
                    <th className={`${tableHeadClassName} w-[30%]`}>Product</th>
                    <th className={tableHeadClassName}>Customer</th>
                    <th className={tableHeadClassName}>Tracking</th>
                    <th className={tableHeadClassName}>Delivery</th>
                    <th className={tableHeadClassName}>Confirmation</th>
                    <th className={`${tableHeadClassName} text-right`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((order) => {
                    const isConfirmed = confirmedIds.includes(order.id);
                    const isPriority = isPriorityOrder(order);
                    const rowHighlight = isPriority
                      ? getPriorityRowHighlightClass(order)
                      : 'hover:bg-slate-50/70';
                    return (
                      <tr
                        key={order.id}
                        className={['group transition-colors', rowHighlight].join(' ')}
                      >
                        <td className={tableCellClassName}>
                          <SellerCentralOrderDate value={order.orderDate} marketplace={order.marketplace} now={now} />
                        </td>
                        <td className={tableCellClassName}>
                          <span className="block font-mono text-xs font-semibold text-slate-800">{order.amazonOrderId}</span>
                          <span className="mt-1 block text-[11px] text-slate-400">{order.marketplace} marketplace</span>
                        </td>
                        <td className={tableCellClassName}>
                          <OrderLineProducts order={order} urgent={isPriority} />
                        </td>
                        <td className={tableCellClassName}>
                          <span className="block font-medium text-slate-900">{order.buyerName || 'Unknown customer'}</span>
                          <span className="mt-1 block text-xs text-slate-500">{order.buyerPhone || 'No phone number'}</span>
                        </td>
                        <td className={`${tableCellClassName} max-w-[10rem] truncate font-mono text-xs`} title={order.trackingUs}>
                          {order.trackingUs || '—'}
                        </td>
                        <td className={tableCellClassName}>
                          <OrderStatusBadge order={order} />
                        </td>
                        <td className={tableCellClassName}>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                            <span
                              className={`h-2 w-2 rounded-full ${
                                isConfirmed ? 'bg-emerald-500' : 'bg-amber-500'
                              }`}
                            />
                            <span
                              className={
                                isConfirmed ? 'text-emerald-800' : 'text-amber-800'
                              }
                            >
                              {isConfirmed ? 'Confirmed' : 'Pending'}
                            </span>
                          </span>
                        </td>
                        <td className={`${tableCellClassName} text-right`}>
                          <button
                            type="button"
                            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-blue-200 hover:text-blue-700"
                            onClick={() => setSelectedOrder(order)}
                          >
                            Review order
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm">
              <p className="text-slate-600">
                Showing {rangeStart}–{rangeEnd} of {total.toLocaleString('en-IN')} delivered order
                {total === 1 ? '' : 's'}
                {days !== 'all' ? ` (last ${days} days)` : ''}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={page <= 1 || status.type === 'loading'}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </button>
                <span className="min-w-[7rem] text-center font-medium text-slate-800">
                  Page {totalPages === 0 ? 0 : page} of {totalPages}
                </span>
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  disabled={totalPages === 0 || page >= totalPages || status.type === 'loading'}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-6 py-14 text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-400">
              <SearchIcon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-slate-900">No orders found</h3>
            <p className="mt-1 text-sm text-slate-500">
              Try another filter, date range, or clear your search.
            </p>
          </div>
        )}
      </div>

      <ReviewOrderModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        isConfirmed={selectedOrder ? confirmedIds.includes(selectedOrder.id) : false}
        isPriority={selectedOrder ? isPriorityOrder(selectedOrder) : false}
        onToggleConfirmation={() => selectedOrder && toggleConfirmation(selectedOrder.id)}
      />

      <ValidationAlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal((prev) => ({ ...prev, isOpen: false }))}
        title={alertModal.title}
        message={alertModal.message}
        missingOrders={alertModal.missingOrders}
      />

      <RemarkHistoryModal
        isOpen={Boolean(remarkHistoryTarget?.lineId || remarkHistoryTarget?.orderId)}
        onClose={() => setRemarkHistoryTarget(null)}
        token={token}
        lineId={remarkHistoryTarget?.lineId}
        orderId={remarkHistoryTarget?.orderId}
      />
    </section>
  );
}

function ReviewOrderModal({ order, onClose, isConfirmed, isPriority, onToggleConfirmation }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!order) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, order]);

  if (!order) return null;

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="crm-panel relative w-full max-w-2xl overflow-hidden border border-slate-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-order-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`h-1 ${isPriority ? 'bg-red-500' : 'bg-blue-600'}`} />
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {isPriority && <Badge tone="danger">Priority</Badge>}
              {isConfirmed && <Badge tone="success">Confirmed</Badge>}
            </div>
            <h2 id="review-order-title" className="text-lg font-semibold tracking-tight text-slate-900">
              Review Delivered Order
            </h2>
            <p className="mt-1 font-mono text-xs text-slate-500">{order.amazonOrderId}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="grid h-9 w-9 place-items-center rounded-lg text-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            onClick={onClose}
            aria-label="Close order review"
          >
            &times;
          </button>
        </div>

        <div className="max-h-[65vh] space-y-6 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Customer</h3>
              <div className="space-y-1.5 text-sm">
                <p className="font-semibold text-slate-900">{order.buyerName || '—'}</p>
                <p className="text-slate-600">{order.buyerPhone || 'No Phone Number'}</p>
                <p className="mt-3 text-xs leading-relaxed text-slate-500">
                  {order.shipAddressLine1 || '—'}<br />
                  {order.shipCity || ''}, {order.shipState || ''} - {order.shipPincode || ''}
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
              <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Delivery</h3>
              <div className="space-y-3 text-sm text-slate-600">
                <div className="flex justify-between">
                  <span>Marketplace:</span>
                  <Badge tone={order.marketplace === 'US' ? 'info' : 'default'}>{order.marketplace}</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Order Date:</span>
                  <span className="font-medium text-slate-800">
                    {order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-IN') : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Expected Delivery:</span>
                  <span className="font-medium text-slate-800">
                    {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('en-IN') : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>US Tracking:</span>
                  <span className="font-mono text-xs font-medium text-slate-800">{order.trackingUs || '—'}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Purchased products</h3>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
              {order.items?.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-4">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="h-12 w-12 flex-shrink-0 rounded-lg border border-slate-200 object-cover" />
                  ) : (
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-xs font-semibold text-slate-400">
                      PKG
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 text-xs font-semibold leading-snug text-slate-900">{item.title}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-slate-500">
                      <span>ASIN: {item.asinIn || item.asinCom || '—'}</span>
                      <span>SKU: {item.sku || '—'}</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs font-semibold text-slate-800">Qty: {item.quantityOrdered}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{order.currency} {Number(item.unitPrice).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/70 px-6 py-4">
          <div className="flex items-center gap-2">
            {!isPriority && (
              <button
                type="button"
                onClick={onToggleConfirmation}
                className={`rounded-md border px-3 py-2 text-xs font-semibold transition ${isConfirmed
                  ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
              >
                {isConfirmed ? '✓ Confirmed' : 'Mark Confirmed'}
              </button>
            )}
          </div>

          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const getUIStatus = (status) => {
  if (!status || status === 'procurement_required') return 'Amazon';
  return status;
};

const getDisplayOrderStatus = (status) => {
  if (!status) return '—';
  const lower = status.toLowerCase();
  if (lower === 'pending' || lower === 'unshipped') return 'unshipped';
  if (lower === 'delivered') return 'delivered';
  if (lower === 'shipped') return 'shipped';
  if (lower === 'cancelled' || lower === 'canceled') return 'cancelled';
  return status;
};

export default CustomerServicePage;


